/**
 * The finance views' shared frame: the tab row under Spend, the scope bar (range, project and
 * account, kept in the URL so a view can be shared), and the gate every report view uses for
 * its loading, error and refreshing states.
 */
import type { FinanceReport, Id } from '@orgus/protocol';
import { RefreshCw } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router';
import { Button, DateRangePicker, ErrorState, RelativeTime, Select, Skeleton, SkeletonRows, Spinner, Tooltip, cn, customRange, dayKey, isDateRangePreset, parseDateRange, parseDayKey, writeDateRange } from '@orgus/ui';
import type { DateRange } from '@orgus/ui';
import { StatStripSkeleton } from '../fleet/StatStrip.tsx';
import { accountOptions, hasAccounts } from '../costs/accounts.tsx';
import { useCosts } from '../costs/useCosts.ts';
import { useOrgus, useProjects } from '../../lib/store.ts';
import { isRangeKey, rangeFor } from './model.ts';
import type { RangeKey } from './model.ts';
import type { ReportResult } from './useFinance.ts';

export const SPEND_TABS = [
  { to: '/spend', label: 'Overview', end: true },
  { to: '/spend/pnl', label: 'P&L' },
  { to: '/spend/reconcile', label: 'Reconciliation' },
  { to: '/spend/ledger', label: 'Ledger' },
  { to: '/spend/model-mix', label: 'Model mix' },
  { to: '/spend/exports', label: 'Exports' },
] as const;

/** The tab row; carries the scope (?range, ?project, ?account) from tab to tab. */
export function SpendTabs() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const keep = new URLSearchParams();
  for (const k of ['range', 'from', 'to', 'project', 'account']) {
    const v = params.get(k);
    if (v) keep.set(k, v);
  }
  const qs = keep.toString() ? `?${keep.toString()}` : '';
  return (
    <nav aria-label="Spend views" className="mb-6 flex h-row items-stretch gap-6 overflow-x-auto border-b border-border-subtle">
      {SPEND_TABS.map((t) => (
        <NavLink
          key={t.to}
          to={`${t.to}${qs}`}
          end={'end' in t ? t.end : false}
          className={({ isActive }) =>
            cn(
              'relative -mb-px inline-flex shrink-0 items-center border-b-2 border-transparent text-ui font-medium text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted',
              isActive && 'border-accent font-semibold text-fg',
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

export interface Scope {
  range: RangeKey;
  from: string;
  to: string;
  projectId: Id | null;
  /** An account key, 'unknown', or null for every account (ADR 0067). */
  account: string | null;
}

export function useScope(): Scope & { dates: DateRange; setDates(r: DateRange): void; setRange(r: RangeKey): void; setProject(id: Id | null): void; setAccount(key: string | null): void } {
  const [params, setParams] = useSearchParams();
  const raw = params.get('range');
  const range: RangeKey = isRangeKey(raw) ? raw : '12w';
  const projectId = params.get('project') || null;
  const account = params.get('account') || null;
  // The shared date range (ADR 0109) when the URL names one; the finance periods otherwise.
  const shared = isDateRangePreset(raw) ? parseDateRange(params, Date.now()) : null;
  const { from, to } = shared ? { from: dayKey(shared.from), to: dayKey(shared.preset === 'custom' ? shared.to - 1 : shared.to) } : rangeFor(range, Date.now());
  const dates = shared ?? customRange(parseDayKey(from) ?? Date.now(), parseDayKey(to) ?? Date.now());
  const setDates = (r: DateRange) =>
    setParams(
      (p) => {
        const n = writeDateRange(r, p, 'custom');
        if (r.preset !== 'custom') n.set('range', r.preset);
        return n;
      },
      { replace: true },
    );
  const set = (k: string, v: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );
  return { range, from, to, projectId, account, dates, setDates, setRange: (r) => set('range', r === '12w' ? null : r), setProject: (id) => set('project', id), setAccount: (key) => set('account', key) };
}

/** A month picker in the scope bar's period slot (Exports), in place of the range. */
export interface ScopeMonth {
  value: string;
  options: { value: string; label: string }[];
  onChange(month: string): void;
}


/**
 * The one scope bar every Spend tab uses: period (or a month), project and account, each
 * unlabelled with an accessible name, the ones a tab does not use hidden, and when the report
 * was made. On a phone the period is a select and the pickers share a two-column grid, so
 * nothing runs off the edge.
 */
export function ScopeBar({
  scope,
  result,
  note,
  month,
  hideRange,
  hideProject,
  hideAccount,
  updatedAt,
}: {
  scope: ReturnType<typeof useScope>;
  result?: ReportResult;
  /** A tab without a report (the ledger): when its data last changed, for the same live caption. */
  updatedAt?: number;
  /** One short caption after the pickers: what the scope leaves out. */
  note?: ReactNode;
  month?: ScopeMonth;
  hideRange?: boolean;
  hideProject?: boolean;
  hideAccount?: boolean;
}) {
  const projects = useProjects();
  const costs = useCosts();
  const showAccount = !hideAccount && (hasAccounts(costs) || scope.account !== null);
  const showRange = !hideRange && !month;
  const live = useOrgus((s) => s.connection === 'live' || s.mode === 'mock');
  const options = [{ value: '', label: 'All projects' }, ...projects.map((p) => ({ value: p.id, label: p.name }))];
  const pickers = (showRange || month ? 1 : 0) + (hideProject ? 0 : 1) + (showAccount ? 1 : 0);
  // Two columns on a phone: a picker left alone on its row takes the full width.
  const span = (i: number) => (pickers % 2 === 1 && i === pickers ? 'col-span-2 md:col-span-1' : '');
  let at = 0;
  const period = showRange || month ? ++at : 0;
  const project = hideProject ? 0 : ++at;
  const account = showAccount ? ++at : 0;
  const anyCaption = (showAccount && scope.account !== null) || note != null;
  return (
    <div className="mb-4 grid grid-cols-2 items-center gap-2 md:flex md:flex-wrap md:gap-3">
      {showRange && <DateRangePicker value={scope.dates} onChange={scope.setDates} className={span(period)} />}
      {month && <Select size="md" aria-label="Month" options={month.options} value={month.value} onValueChange={month.onChange} wrapperClassName={cn('md:w-44', span(period))} />}
      {!hideProject && <Select size="md" aria-label="Project" options={options} value={scope.projectId ?? ''} onValueChange={(v) => scope.setProject(v || null)} wrapperClassName={cn('min-w-0 md:w-56', span(project))} />}
      {showAccount && <Select size="md" aria-label="Account" options={accountOptions(costs)} value={scope.account ?? ''} onValueChange={(v) => scope.setAccount(v || null)} wrapperClassName={cn('min-w-0 md:w-56', span(account))} />}
      {anyCaption && (
        <span className={cn('truncate text-caption text-fg-subtle md:col-span-1', result?.report ? 'col-span-1' : 'col-span-2')}>
          {showAccount && scope.account !== null ? 'Token spend only' : note}
        </span>
      )}
      {(result?.report || updatedAt !== undefined) && (
        <span className={cn('flex items-center justify-end gap-1 text-caption text-fg-subtle md:col-span-1 md:ml-auto', anyCaption ? 'col-span-1' : 'col-span-2')}>
          {result?.refreshing ? (
            <>
              <Spinner className="size-3" /> Updating
            </>
          ) : (
            <Tooltip
              content={
                !live ? 'Not live: the hub connection dropped' : result?.report ? 'Rebuilt when costs, entries or a pull change, and every minute' : 'Updates as entries, subscriptions or a pull change'
              }
            >
              <span tabIndex={0}>
                {live ? 'Live' : 'Paused'}, {result?.report ? 'built' : 'updated'} <RelativeTime ts={result?.report?.generatedAt ?? updatedAt ?? 0} />
              </span>
            </Tooltip>
          )}
        </span>
      )}
    </div>
  );
}

export function ReportSkeleton({ tiles = 6 }: { tiles?: number }) {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading the finance report">
      {tiles > 0 && <StatStripSkeleton cells={tiles} />}
      <Skeleton className="h-64" />
      <div className="rounded-card border border-border bg-surface">
        <SkeletonRows rows={5} />
      </div>
    </div>
  );
}

/** Loading, error and refreshing for a report view; renders children with the report. */
export function ReportGate({ result, children, skeleton }: { result: ReportResult; children: (report: FinanceReport) => ReactNode; skeleton?: ReactNode }) {
  if (result.error && !result.report) {
    return (
      <div className="rounded-card border border-border bg-surface">
        <ErrorState error={result.error} onRetry={result.retry} />
      </div>
    );
  }
  if (!result.report) return <>{skeleton ?? <ReportSkeleton />}</>;
  return (
    <>
      {result.error && (
        <div role="status" className="mb-4 flex items-center gap-3 rounded-card border border-status-attention-line bg-status-attention-soft px-row-x py-2 text-ui text-fg">
          <RefreshCw className="size-4 shrink-0 text-status-attention" aria-hidden />
          <span className="min-w-0 flex-1">
            Showing the last report. {result.error.what} {result.error.next}
          </span>
          <Button size="sm" variant="ghost" onClick={result.retry}>
            Retry
          </Button>
        </div>
      )}
      {children(result.report)}
    </>
  );
}

/** A missing value in a figure column: a quiet dash that never reads as data; `label` names it for screen readers. */
export function Dash({ label }: { label: string }) {
  return (
    <span className="text-fg-subtle">
      <span aria-hidden>–</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** A KPI tile: value, unit in muted text, a caveat line, optional status chip. */
export function Tile({ label, value, unit, sub, children, tone }: { label: string; value: ReactNode; unit?: string; sub: ReactNode; children?: ReactNode; tone?: 'waiting' }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-card border border-border bg-surface px-4 py-3">
      <span className="truncate text-caption text-fg-muted">{label}</span>
      <span className="flex items-baseline gap-1">
        <span className={cn('truncate text-heading num', tone === 'waiting' ? 'text-status-attention' : 'text-fg')}>{value}</span>
        {unit && <span className="shrink-0 text-ui text-fg-subtle">{unit}</span>}
      </span>
      {children}
      <span className="truncate text-caption text-fg-subtle">{sub}</span>
    </div>
  );
}
